<template>
  <div>
    <b-tabs pills card vertical>
      <template #tabs-end>
        <b-nav-item
          v-for="role in roles"
          :key="role"
          href="#"
          role="presentation"
          @click="setActiveRole(role)"
          :active="activeRole === role"
          >{{ roleLabels[role] }}</b-nav-item
        >
      </template>
      <div class="py-2">
        <table class="table">
          <tr>
            <td></td>
            <td class="text-center font-weight-bold">Read</td>
            <td class="text-center font-weight-bold">Create</td>
            <td class="text-center font-weight-bold">Update</td>
            <td class="text-center font-weight-bold">Delete</td>
          </tr>
          <tr v-for="([chunkName, chunkEntries], chunkIndex) in regularPermissionsChunks" :key="chunkIndex">
            <td class="font-weight-bold">{{ getChunkLabel(chunkName) }}</td>
            <td class="text-center" v-for="(permission, i) in chunkEntries" :key="i">
              <input
                type="checkbox"
                :checked="!!enabledPermissionsMap[permission]"
                :disabled="permissionLockedMap[permission] || superAdminSelected || isPermissionInProgress(permission)"
                @change="togglePermission($event, permission)"
              />
            </td>
          </tr>
          <tr>
            <th colspan="100%" class="border-top-0">
              <h5 class="h5 mt-3 mb-2">Special Permissions</h5>
            </th>
          </tr>
          <tr v-for="permission in complexAndSpecialPermissions" :key="permission">
            <td class="font-weight-bold">{{ COMPLEX_AND_SPECIAL_PERMISSION_TITLES[permission] || permission }}</td>
            <td class="text-center">
              <input
                type="checkbox"
                :checked="!!enabledPermissionsMap[permission]"
                :disabled="permissionLockedMap[permission] || superAdminSelected || isPermissionInProgress(permission)"
                @change="togglePermission($event, permission)"
              />
            </td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </table>
      </div>
    </b-tabs>
  </div>
</template>

<script>
import * as PN from '@/utils/PermissionNames'
import { Api, post } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'

const authItemsApi = new Api(UrlStore.AUTH_ITEMS)
const authPermissionsApi = new Api(UrlStore.AUTH_PERMISSIONS)

const SUPER_ADMIN_ROLE = 'superAdminRole'

const roleLabels = {
  superAdminRole: 'Owner',
  adminRole: 'Admin',
  userRole: 'User'
}

const PERMISSIONS_LOCKED = [
  PN.EMPLOYEE_READ,
  PN.EMPLOYEE_POSITION_READ,
  PN.BUILDING_READ,
  PN.FLOOR_READ,
  PN.POINT_READ,
  PN.ROOM_READ,
  PN.PRINTER_READ,
  PN.DESK_READ,
  PN.DESK_EVENT_READ,
  PN.PC_READ,
  PN.DEPARTMENT_READ
]

const PERMISSION_CHUNK_TITLES = {
  PC: 'PC'
}

const COMPLEX_AND_SPECIAL_PERMISSION_TITLES = {
  [PN.DESK_EVENT_CREATE_INFINITY]: 'Desk Infinity Booking',
  [PN.FILE_CRUD]: 'Files Attaching',
  [PN.MAP_REQUEST_CRUD]: 'Request Map Updates',
  [PN.USER_CRUD]: 'Users Control',
  [PN.PERMISSION_CRUD]: 'Permission Control'
}

const PERMISSION_TYPE_ORDER = ['READ', 'CREATE', 'UPDATE', 'DELETE']

function permissionTypeSort(a, b) {
  return PERMISSION_TYPE_ORDER.indexOf(a) - PERMISSION_TYPE_ORDER.indexOf(b)
}

export default {
  name: 'Permissions',
  data() {
    return {
      activeRole: '',
      authItems: [],
      permissionBindings: [],
      permissionUpdateProcessing: {},
      COMPLEX_AND_SPECIAL_PERMISSION_TITLES
    }
  },
  async mounted() {
    await this.getAuthItems()
    await this.getPermissionBindings()
    this.setActiveRole(this.roles[0] ?? '')
  },
  computed: {
    permissionLockedMap() {
      return Object.fromEntries(PERMISSIONS_LOCKED.map((v) => [v, true]))
    },
    superAdminSelected() {
      return this.activeRole === SUPER_ADMIN_ROLE
    },
    enabledPermissionsMap() {
      return Object.fromEntries(this.permissionBindings.filter(({ parent }) => parent === this.activeRole).map(({ child }) => [child, true]))
    },
    permissions() {
      return this.authItems.filter((v) => v.type === 2).map(({ name }) => name)
    },
    roles() {
      return this.authItems.filter((v) => v.type === 1).map(({ name }) => name)
    },
    roleLabels() {
      return Object.fromEntries(this.roles.map((role) => [role, roleLabels[role] ?? role]))
    },
    regularPermissionsChunks() {
      const chunks = this.permissions.reduce((acc, permission) => {
        if (permission === PN.DESK_EVENT_CREATE_INFINITY) {
          return acc
        }

        if (permission.endsWith('_CRUD')) {
          return acc
        }

        const chunk = permission
          .split('_')
          .slice(0, -1)
          .join('_')

        if (!acc[chunk]) {
          acc[chunk] = []
        }

        acc[chunk].push(permission)

        return acc
      }, [])

      Object.values(chunks).forEach((chunk) => chunk.sort(permissionTypeSort))

      return Object.entries(chunks)
    },
    complexAndSpecialPermissions() {
      return this.permissions.filter((permission) => permission === PN.DESK_EVENT_CREATE_INFINITY || permission.endsWith('_CRUD'))
    }
  },
  methods: {
    isPermissionInProgress(permission) {
      return this.permissionUpdateProcessing[this.activeRole]?.[permission]
    },
    async togglePermission(event, permission) {
      this.$set(this.permissionUpdateProcessing[this.activeRole], permission, true)
      event.target.checked = !this.enabledPermissionsMap[permission]
      const permissionEntity = {
        permission,
        role: this.activeRole,
        action: !this.enabledPermissionsMap[permission]
      }
      try {
        await this.savePermission(permissionEntity)
      } catch (e) {
        if (!e.isAxiosErorr) {
          throw e
        }
      } finally {
        await this.getPermissionBindings()
        this.$set(this.permissionUpdateProcessing[this.activeRole], permission, false)
      }
    },
    getChunkLabel(chunkName) {
      return (
        PERMISSION_CHUNK_TITLES[chunkName] ??
        chunkName
          .split('_')
          .map((part) => {
            return part[0].toUpperCase() + part.slice(1).toLowerCase()
          })
          .join(' ')
      )
    },
    setActiveRole(role) {
      this.activeRole = role
      if (!this.permissionUpdateProcessing[role]) {
        this.$set(this.permissionUpdateProcessing, role, {})
      }
    },
    async getAuthItems() {
      const { data } = await authItemsApi.getAll()
      this.authItems = data.items
    },
    async getPermissionBindings() {
      const { data } = await authPermissionsApi.getAll()
      this.permissionBindings = data.items
    },
    savePermission(permissionEntity) {
      return post(UrlStore.AUTH_PERMISSIONS, permissionEntity)
    }
  }
}
</script>
